/**
 * Portfolio Manager
 * Gère l'affichage du portfolio avec :
 * - Modal Bootstrap pour la navigation
 * - Swiper pour les diaporamas (projets et photos)
 * - Macy pour les grilles masonry
 */

import Swiper from 'swiper';
import { Navigation, Pagination, Autoplay, Parallax, Scrollbar, EffectFade, EffectCoverflow, EffectFlip, HashNavigation, History, Zoom } from 'swiper/modules';
import Macy from 'macy';

Swiper.use([Navigation, Pagination, Autoplay, Parallax, Scrollbar, EffectFade, EffectCoverflow, EffectFlip, HashNavigation, History, Zoom]);

// ============================================================================
// GLOBAL STATE
// ============================================================================

const state = {
    modal: null,
    modalElement: null,
    isModalFullyShown: false,
    launcherProjects: null,
    swiperProjects: null,
    swiperProjectInstances: {},
    masonryInstances: {},
    historyRoot: '/'
};

// ============================================================================
// TOOLS
// ============================================================================

/**
 * Calcule la racine de l'historique pour les URLs
 * Exemples: /fr/portfolio/slug -> /fr/, /portfolio/slug -> /
 */
function calculateHistoryRoot() {
    const path = window.location.pathname;
    const parts = path.split('/').filter(Boolean);
    const portfolioIndex = parts.indexOf('portfolio');

    // Cas standard: /fr/portfolio/...
    if (portfolioIndex > 0) {
        return '/' + parts.slice(0, portfolioIndex).join('/') + '/';
    }

    // Cas langue seule: /fr
    if (parts.length === 1 && /^[a-z]{2}$/i.test(parts[0])) {
        return '/' + parts[0] + '/';
    }

    // Pas de préfixe => racine
    return '/';
}

/**
 * Restaure l'URL de base en retirant /portfolio/slug
 */
function restoreBaseUrl() {
    const currentPath = window.location.pathname;
    const cleanPath = currentPath.replace(/\/portfolio\/[^/]+\/?$/, '');
    window.history.replaceState({}, '', cleanPath || '/');
}

/**
 * Parse l'URL pour extraire le slug du projet si présent
 */
function getProjectSlugFromUrl() {
    const urlParts = window.location.pathname.split('/').filter(Boolean);

    if (urlParts[0] === 'portfolio' && urlParts[1]) {
        return urlParts[1];
    }

    return null;
}

// ============================================================================
// MASONRY
// ============================================================================

/**
 * Initialise toutes les grilles masonry
 */
function initMasonryGrids() {
    document.querySelectorAll('.masonry-grid[data-slug]').forEach(gridElement => {
        const projectSlug = gridElement.dataset.slug;

        const masonryInstance = new Macy({
            container: `.masonry-grid[data-slug=${projectSlug}]`,
            trueOrder: true,
            waitForImages: true,
            resizeObserver: true,
            margin: 16,
            columns: 4,
            breakAt: {
                1080: { columns: 4, margin: { x: 16 } },
                768: { columns: 3, margin: { x: 8 } }
            }
        });

        // Stockage global pour accès ultérieur
        state.masonryInstances[projectSlug] = masonryInstance;
        window.macyInstances = state.masonryInstances;

        // Logs de debug
        masonryInstance.on(masonryInstance.constants.EVENT_IMAGE_COMPLETE, () => {
            //console.log(`[Masonry] Images chargées pour: ${projectSlug}`);
            if (state.isModalFullyShown) {
                projectsUpdateSize();
            }
        });

        // masonryInstance.on(masonryInstance.constants.EVENT_INITIALIZED, () => {
        //     console.log(`[Masonry] Initialisé pour: ${projectSlug}`);
        // });
    });
}

/**
 * Recal Masonry
 */
function recalculateMasonry(projectSlug) {
    if (state.masonryInstances[projectSlug]) {
        state.masonryInstances[projectSlug].recalculate(true);

        projectsUpdateSize(); // swiper
    }
}

function recalculateAllMasonry() {
    //console.log('[Masonry] Recalcul global de tous les masonry');
    Object.keys(state.masonryInstances).forEach(slug => {
        state.masonryInstances[slug].recalculate(true);
    });
}

// ============================================================================
// SWIPERS
// ============================================================================


/**
 * Init MasonrySwipers
 */
function initProjectsSwiper() {
    state.swiperProjects = new Swiper('.swiper-projects', {
        direction: 'horizontal',
        loop: true,
        slidesPerView: 1,
        spaceBetween: 8,
        speed: 1200,
        autoHeight: false,
        autoplay: true,
        hashNavigation: false,
        history: {
            enabled: true,
            keepQuery: true,
            replaceState: false,
            root: state.historyRoot,
            key: 'portfolio',
        },
        parallax: true,
        navigation: {
            nextEl: '.swiper-buttons[data-slug=projects] .swiper-button-next',
            prevEl: '.swiper-buttons[data-slug=projects] .swiper-button-prev',
        },
        pagination: false,
        scrollbar: false,
        observer: true,
        observeParents: true,
        observeSlideChildren: true,
    });

    // Écoute du changement de slide pour recalculer le masonry
    //state.swiperProjects.on('slideChange', handleProjectsSlideChange);
}

/**
 * Init Modal Swipers
 */
function initProjectSwipers() {
    document.querySelectorAll('.swiper-project').forEach(sliderElement => {
        const projectSlug = sliderElement.dataset.projectSlug;

        if (!projectSlug) return;

        const swiperInstance = new Swiper(`.swiper-project[data-project-slug=${projectSlug}]`, {
            direction: 'horizontal',
            slidesPerView: 1,
            spaceBetween: 16,
            speed: 400,
            autoHeight: true,
            navigation: {
                nextEl: '.swiper-buttons[data-slug=project] .swiper-button-next',
                prevEl: '.swiper-buttons[data-slug=project] .swiper-button-prev',
            },
            pagination: false,
            scrollbar: false,
            // scrollbar: {
            //     el: '.swiper-buttons[data-slug=project] .swiper-scrollbar',
            // },
            zoom: true,
            breakpoints: {
                768: {
                    zoom: true,
                },
                1024: {
                    zoom: false,
                },
            },
        });

        state.swiperProjectInstances[projectSlug] = swiperInstance;
    });
}

/**
 * Gère le changement de slide dans le swiper des projets
 */
function handleProjectsSlideChange() {
    const activeSlide = state.swiperProjects.slides[state.swiperProjects.activeIndex];
    const gridElement = activeSlide.querySelector('.masonry-grid');
    const projectSlug = gridElement?.dataset.slug;

    if (!projectSlug) return;

    // Mise à jour de l'attribut data
    state.modalElement.dataset.projectActive = projectSlug;

    // Recalcul du masonry si changement de projet
    const previousProjectSlug = state.modalElement.dataset.projectActive;
    if (projectSlug !== previousProjectSlug) {
        setTimeout(() => {
            recalculateMasonry(projectSlug);
        }, 100);
    }
}

// ============================================================================
// UX - Display
// ============================================================================

/**
 * Affiche la galerie d'un projet spécifique
 */
function showProjectGallery(projectSlug, slideIndex = 0) {
    // state.modalElement.classList.remove('display-projects');
    // state.modalElement.classList.add('display-project');

    // Open Modal
    state.modal.show();

    // Active le projet ciblé
    document.querySelectorAll('.swiper-project').forEach(el => {
        el.classList.remove('project-active');
    });

    const targetProject = document.querySelector(`.swiper-project[data-project-slug=${projectSlug}]`);
    targetProject?.classList.add('project-active');

    // Mise à jour du slug actif
    state.modalElement.dataset.slug = projectSlug;

    // Navigation vers la slide demandée
    const swiperInstance = state.swiperProjectInstances[projectSlug];
    if (swiperInstance) {
        swiperInstance.slideTo(slideIndex);
    }
}

/**
 * Affiche la liste des projets (vue principale)
 */
function showProjectsList() {
    state.modalElement.classList.add('display-projects');
    state.modalElement.classList.remove('display-project');
}

/**
 * Recalcule la hauteur du container de swiper
 */
function projectsUpdateSize() {
    state.swiperProjects.updateSize();
}

// ============================================================================
// EVENTS
// ============================================================================

/**
 * Initialise les événements sur les vignettes masonry
 */
function initMasonryClickEvents() {
    const masonryLinks = document.querySelectorAll('.masonry-grid .links');

    masonryLinks.forEach(link => {
        link.addEventListener('click', event => {
            event.preventDefault();

            const projectSlug = link.dataset.projectSlug;
            const slideIndex = parseInt(link.dataset.slideIndex, 10);

            showProjectGallery(projectSlug, slideIndex);
        });
    });
}

/**
 * Initialise masonry again when modal is open
 */
function initMasonryModalEvents() {

    state.modalElement.addEventListener('show.bs.modal', function () {
        state.isModalFullyShown = false;
    });

    state.modalElement.addEventListener('shown.bs.modal', function () {
        state.isModalFullyShown = true;
        const activeProjectSlug = state.modalElement.dataset.projectActive;

        setTimeout(() => {
            recalculateAllMasonry();
        }, 50);

        //console.log('[Modal] Ouverture terminée - Recalcul des layouts');
        setTimeout(() => {
            //recalculateMasonry(activeProjectSlug);
            projectsUpdateSize();
        }, 150);
    });

    state.modalElement.addEventListener('hidden.bs.modal', function () {
        state.isModalFullyShown = false;
    });
}

/**
 * Initialise les boutons de fermeture
 */
function initCloseButtons() {
    const closeProjectsButton = state.modalElement.querySelector('.swiper-button-close-projects');
    const closeProjectButton = state.modalElement.querySelector('.swiper-button-close-project');

    // Fermeture de la modal (depuis la liste des projets)
    closeProjectButton.addEventListener('click', () => {
        const activeProjectSlug = state.modalElement.dataset.projectActive;
        restoreBaseUrl();
        state.modal.hide();
    });
}

// ============================================================================
// INIT
// ============================================================================

/**
 * Gère l'ouverture automatique si URL contient un projet
 */
function handleInitialUrl() {
    const projectSlug = getProjectSlugFromUrl();

    if (projectSlug) {
        state.modalElement.dataset.projectActive = projectSlug;
        state.modal.show();
        showProjectsList();
    }
}

/**
 * Point d'entrée principal
 */
function initPortfolio() {
    // Calcul de la racine pour l'historique
    state.historyRoot = calculateHistoryRoot();

    // Récupération des éléments DOM
    const portfolioModalElement = document.getElementById('portfolio');
    if (!portfolioModalElement) {
        console.warn('[Portfolio] Modal #portfolio introuvable');
        return;
    }

    state.modal = new Modal(portfolioModalElement);
    state.modalElement = portfolioModalElement;

    // Initialisation des composants
    initMasonryGrids();
    initProjectsSwiper();
    initProjectSwipers();

    // Initialisation des événements
    initMasonryClickEvents();
    //initMasonryModalEvents();
    initCloseButtons();

    // Gestion de l'URL initiale
    //handleInitialUrl();
}

// ============================================================================
// RUN
// ============================================================================

document.addEventListener('DOMContentLoaded', initPortfolio);
